import React from 'react'
import { PiEye } from 'react-icons/pi'
// eslint-disable-next-line no-restricted-imports -- Phosphor's ellipsis glyphs render too thin; DS2026 ⋯ menus use lucide.
import { Ellipsis } from 'lucide-react'
import { cn } from 'components/UI/cn'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import { ds2026DropdownContentClass } from 'components/FbUI/ds2026DropdownMenu'
import { useModalDepth } from 'components/UI/Modal/ModalPrimitive'
import { getDropdownZIndex } from 'styles/z-index'
import { EmailThread } from 'models/emailIntegration'
import { ComposeMode } from './ComposePanel'
import {
  EmailDealActionCallbacks,
  buildActionContext,
  useEmailThreadActions,
  EmailAction,
} from './emailActions'
import { EmailActionMenuItems } from 'components/Email'

interface InboxRowMenuProps extends EmailDealActionCallbacks {
  thread: EmailThread
  isSelected: boolean
  isOpen?: boolean
  onView: (thread: EmailThread) => void
  onCompose: (thread: EmailThread, mode: ComposeMode) => void
  assignDealSubContent?: React.ReactNode
  onOpenChange?: (open: boolean) => void
}

/** 3-dot menu shown on inbox row hover, overlaying the timestamp column. */
export function InboxRowMenu({
  thread,
  isSelected,
  isOpen,
  onView,
  onCompose,
  assignDealSubContent,
  onOpenChange,
  ...dealCallbacks
}: InboxRowMenuProps) {
  const modalDepth = useModalDepth()
  const zIndex = React.useMemo(
    () => getDropdownZIndex(modalDepth),
    [modalDepth]
  )

  const handleCompose = React.useCallback(
    (mode: ComposeMode) => onCompose(thread, mode),
    [onCompose, thread]
  )

  const threadActions = useEmailThreadActions(
    buildActionContext(
      dealCallbacks,
      handleCompose,
      assignDealSubContent,
      thread.is_hidden,
      thread.is_own_thread
    )
  )

  // Prepend a "View" action before the shared actions
  const viewAction: EmailAction = React.useMemo(
    () => ({
      label: 'View',
      icon: PiEye,
      onClick: () => onView(thread),
      disabled: false,
      hasSeparatorAfter: true,
    }),
    [onView, thread]
  )

  // Row menu only shows actionable items — hide disabled actions.
  // Strip separators that would end up at the bottom after filtering.
  const actions = React.useMemo(() => {
    const filtered = [viewAction, ...threadActions.filter((a) => !a.disabled)]
    // Remove separator from last item since nothing follows it
    if (filtered.length > 0) {
      const last = filtered[filtered.length - 1]
      if (last.hasSeparatorAfter) {
        filtered[filtered.length - 1] = { ...last, hasSeparatorAfter: false }
      }
    }
    return filtered
  }, [viewAction, threadActions])

  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className={cn(
            // Same sage-on-white family as the rows, but contrast flips with the
            // row: on a selected (darker sage) row go LIGHTER; on a default/hover
            // (near-white) row go a step deeper so it stays visible.
            'flex items-center justify-center size-7 rounded transition-colors shadow-none',
            isSelected
              ? cn(
                  'hover:bg-surface-row-hover',
                  isOpen &&
                    'bg-surface-row-hover shadow-[0px_1px_2px_rgba(16,24,40,0.12)]'
                )
              : cn(
                  'hover:bg-surface-active',
                  isOpen &&
                    'bg-surface-active shadow-[0px_1px_2px_rgba(16,24,40,0.12)]'
                )
          )}
        >
          <Ellipsis
            className={cn(
              'size-4',
              isSelected || isOpen ? 'text-neutral-800' : 'text-neutral-500'
            )}
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        style={{ minWidth: 200, zIndex }}
        className={ds2026DropdownContentClass}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <EmailActionMenuItems actions={actions} />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
